import { isNumber, notEmpty, Nullable } from "@tager/admin-services";
import { OptionType, SingleFileInputValueType } from "@tager/admin-ui";
import { getNameWithDepth } from "@tager/admin-services";
import { FieldUnion, RepeaterFieldConfig } from "@tager/admin-dynamic-field";

import {
  BrandType,
  CategoryType,
  ConditionType,
  FullCategoryType,
  ProductTypeType,
  CategoryRelatedType,
  FieldType,
} from "@/typings/model";
import {
  CategoryCreationPayload,
  CategoryUpdatePayload,
  ConditionUpdateType,
} from "@/services/requests";
import {
  convertFieldUnionToArrayOfInputs,
  createFileInputValue,
} from "@/utils/common";

export type FieldConditionFormValue = {
  field: Nullable<OptionType<number>>;
  operator: Nullable<OptionType>;
  checkbox: boolean;
  number: string;
  options: Array<OptionType<number>>;
};

export type ConditionFormValue = {
  type: Nullable<OptionType>;
  productType: Nullable<OptionType<number>>;
  fieldConditions: Array<FieldConditionFormValue>;
  operator: Nullable<OptionType>;
  operand: string;
  brands: Array<OptionType<number>>;
};

export type FormValues = {
  name: string;
  type: Nullable<OptionType>;
  parent: Nullable<OptionType<Nullable<number>>>;
  urlPath: string;
  content: Nullable<string>;
  related: Array<CategoryRelatedType>;
  image: Nullable<SingleFileInputValueType>;

  pageDescription: Nullable<string>;
  openGraphImage: Nullable<SingleFileInputValueType>;
  pageTitle: Nullable<string>;

  conditions: Array<ConditionFormValue>;

  throughNavigation: boolean;
  filters: Array<{
    label: string;
    fields: Array<OptionType<number>>;
    description: string;
    coefficient: string;
  }>;
  cross: Array<{
    name: string;
    items: Array<{ name: string; category: Nullable<OptionType<number>> }>;
  }>;
  crossRoot: Nullable<{ id: number; name: string }>;
};

export const typeOptionList = [
  { value: "CATALOG", label: "Разводящая" },
  { value: "PRODUCT", label: "Товарная" },
];

export const typeConditionOptionList = [
  { value: "PRODUCT_TYPE", label: "Тип товара" },
  { value: "BRAND", label: "Производитель" },
  { value: "PRICE", label: "Цена" },
];

export const operatorOptionList = [
  { value: "MORE", label: "Больше" },
  { value: "LESS", label: "Меньше" },
  { value: "EQUAL", label: "Равно" },
];

function convertConditionsToConditionFormValues(
  conditions: Array<ConditionType>
): Array<ConditionFormValue> {
  return conditions.map((condition) => {
    const currentTypeCondition = typeConditionOptionList.find(
      (typeCondition) => typeCondition.value === condition.type
    );

    const currentOperatorOption = operatorOptionList.find(
      (operator) => operator.value === condition.operator
    );

    return {
      type: currentTypeCondition ?? null,
      productType: condition.productType
        ? {
            value: condition.productType.id,
            label: condition.productType.name,
          }
        : null,
      fieldConditions:
        condition.fieldConditions?.map((fieldCondition) => {
          const currentFieldConditionsOperatorOption = operatorOptionList.find(
            (operator) => operator.value === fieldCondition?.operator
          );
          return {
            field: {
              value: fieldCondition.field.id,
              label: fieldCondition.field.name,
            },
            checkbox: !isNumber(fieldCondition.operand)
              ? fieldCondition.operand ?? false
              : false,
            operator: currentFieldConditionsOperatorOption ?? null,
            number: isNumber(fieldCondition.operand)
              ? String(fieldCondition.operand)
              : "0",
            options:
              fieldCondition.options?.map((option) => ({
                value: option.id,
                label: option.name,
              })) ?? [],
          };
        }) ?? [],
      brands:
        condition.brands?.map((brand) => ({
          value: brand.id,
          label: brand.name,
        })) ?? [],
      operator: currentOperatorOption ?? null,
      operand: condition.operand ? String(condition.operand) : "0",
    };
  });
}

export function convertCategoryToFormValues(
  category: Nullable<FullCategoryType>,
  categoryNoChildrenOption: Nullable<Array<OptionType<Nullable<number>>>>,
  categoryOptionList: Array<OptionType>
): FormValues {
  if (!category) {
    return {
      name: "",
      parent: null,
      urlPath: "",
      type: null,
      content: "",
      related: [],
      image: null,

      pageDescription: "",
      openGraphImage: null,
      pageTitle: "",

      conditions: [],

      throughNavigation: false,
      cross: [],
      filters: [],
      crossRoot: null,
    };
  }

  const currentTypeOption = typeOptionList.find(
    (option) => option.value === category.type
  );

  const currentCategoryOption = categoryNoChildrenOption?.find(
    (option) => option.value === category.parentId
  );

  const setThroughNavigation = () => {
    if (category.cross) {
      return true;
    } else if (category.crossRoot) {
      return true;
    }
    return false;
  };

  const convertCrossToFormValues = () => {
    return (
      category.cross?.map((crossItem) => ({
        name: crossItem.name,
        items: crossItem.items.map((item) => {
          const currentCategoryOptionList = categoryOptionList.find(
            (option) => Number(option.value) === item.categoryId
          );
          return {
            name: item.name,
            category: currentCategoryOptionList
              ? {
                  value: Number(currentCategoryOptionList.value),
                  label: currentCategoryOptionList.label,
                }
              : null,
          };
        }),
      })) ?? []
    );
  };

  const convertFiltersToFormValues = () => {
    return category.filters.map((filter) => ({
      label: filter.label,
      fields: filter.fields?.map((field) => ({
        label: field.name,
        value: field.id,
      })),
      description: filter.description,
      coefficient: String(filter.coefficient),
    }));
  };

  return {
    name: category.name ?? "",
    urlPath: category.urlPath ?? "",
    type: currentTypeOption ?? null,
    parent: currentCategoryOption ?? null,
    content: category.content ?? "",
    related: category.related ?? [],
    image: category.image ? createFileInputValue(category.image) : null,

    pageDescription: category.pageDescription ?? "",
    openGraphImage: category.openGraphImage
      ? createFileInputValue(category.openGraphImage)
      : null,
    pageTitle: category.pageTitle ?? "",

    conditions: convertConditionsToConditionFormValues(category.conditions),

    throughNavigation: setThroughNavigation(),
    cross: convertCrossToFormValues(),
    crossRoot: category.crossRoot,

    filters: convertFiltersToFormValues(),
  };
}

export function convertFormValuesToCategoryCreationPayload(
  values: FormValues
): CategoryCreationPayload {
  return {
    name: values.name ?? "",
    type: values.type?.value ?? "",
    parent: values.parent?.value ?? null,
  };
}

function convertFieldConditionFormValuesToUpdateValue(
  fieldConditions: Array<FieldConditionFormValue>
): Array<{
  fieldId: Nullable<number>;
  operator: Nullable<string>;
  operand: Nullable<number | boolean>;
  optionIds: Array<number>;
}> {
  return fieldConditions.map((fieldCondition) => ({
    fieldId: fieldCondition.field?.value ?? null,
    operator: fieldCondition.operator?.value ?? null,
    operand: fieldCondition.checkbox
      ? fieldCondition.checkbox
      : fieldCondition.number
      ? Number(fieldCondition.number)
      : null,
    optionIds: fieldCondition.options?.map((option) => option.value) ?? [],
  }));
}

function convertConditionFormValuesToUpdateValue(
  conditions: Array<ConditionFormValue>
): Array<ConditionUpdateType> {
  return conditions.map((condition) => {
    const typeValue = condition.type?.value ?? null;
    if (typeValue === "PRODUCT_TYPE") {
      return {
        type: typeValue,
        productTypeId: condition.productType?.value ?? null,
        fieldConditions: convertFieldConditionFormValuesToUpdateValue(
          condition.fieldConditions
        ),
        brandIds: [],
        operator: null,
        operand: null,
      };
    }
    if (typeValue === "BRAND") {
      return {
        type: typeValue,
        productTypeId: null,
        fieldConditions: [],
        brandIds: condition.brands?.map((brand) => brand.value),
        operator: null,
        operand: null,
      };
    }
    /** typeValue === 'PRICE' */
    return {
      type: typeValue,
      productTypeId: null,
      fieldConditions: [],
      brandIds: [],
      operator: condition.operator?.value ?? null,
      operand: Number(condition.operand),
    };
  });
}

export function convertFormValuesToCategoryUpdatePayload(
  values: FormValues,
  inputCategoryValues: FieldUnion
): CategoryUpdatePayload {
  return {
    urlPath: values.urlPath ?? "",
    content: values.content ?? "",
    image: values.image?.file.id ?? null,
    related: convertFieldUnionToArrayOfInputs<{
      category: string;
      label: string;
    }>(inputCategoryValues).map((related) => ({
      id: Number(related.category),
      label: related.label,
    })),
    pageDescription: values.pageDescription ?? "",
    openGraphImage: values.openGraphImage?.file.id ?? null,
    pageTitle: values.pageTitle ?? "",
    conditions: convertConditionFormValuesToUpdateValue(values.conditions),
    ...convertFormValuesToCategoryCreationPayload(values),

    filters: values.filters.map((filter) => ({
      label: filter.label,
      fields: filter.fields.map((field) => field.value),
      description: filter.description,
      coefficient: filter.coefficient,
    })),

    cross: values.throughNavigation
      ? values.cross.map((crossItem) => ({
          name: crossItem.name,
          items: crossItem.items.map((item) => ({
            name: item.name,
            categoryId: item.category?.value ?? null,
          })),
        }))
      : null,
  };
}

export function convertListToOption(
  convertList: Array<ProductTypeType | BrandType>
): Array<OptionType<number>> {
  if (!convertList) return [];
  return convertList.map((field) => ({
    value: field.id,
    label: field.name,
  }));
}

export function getFieldOptionList(
  fieldList: Array<FieldType>,
  conditions: Array<ConditionFormValue>
): Array<OptionType<number>> {
  return fieldList
    .map((fieldOption) => {
      const isFindProductType = conditions.some(
        (condition) =>
          condition.productType?.value === fieldOption.productType.id
      );

      if (isFindProductType) {
        return {
          value: fieldOption.id,
          label: `${fieldOption.productType.name}: ${fieldOption.name}`,
        };
      }
      return null;
    })
    .filter(notEmpty)
    .sort();
}

export function convertCategoryListNoChildrenToOption(
  categoryList: Array<CategoryType>,
  isCreation: boolean,
  currentCategory: string
): Array<OptionType<Nullable<number>>> {
  if (!isCreation && categoryList) {
    const indexCurrentCategory = categoryList.findIndex(
      (category) => category.id === Number(currentCategory)
    );

    const filteredArray: Array<CategoryType> = [];

    let i = 0;

    categoryList.forEach((category, index) => {
      if (
        (index === indexCurrentCategory ||
          (index > indexCurrentCategory &&
            categoryList[indexCurrentCategory].depth < category.depth)) &&
        !i
      ) {
        return;
      } else {
        if (
          index > indexCurrentCategory &&
          categoryList[indexCurrentCategory].depth === category.depth
        ) {
          i = 1;
        }
        filteredArray.push(category);
      }
    });
    const optionList: Array<OptionType<Nullable<number>>> = filteredArray.map(
      (category) => ({
        value: category.id,
        label: getNameWithDepth(category.name, category.depth),
      })
    );

    optionList.unshift({ value: null, label: "Корневая категория" });

    return optionList;
  }
  const optionList: Array<OptionType<Nullable<number>>> = categoryList.map(
    (category) => ({
      value: category.id,
      label: getNameWithDepth(category.name, category.depth),
    })
  );
  optionList.unshift({ value: null, label: "Корневая категория" });

  return optionList;
}

export function convertCategoryListToOption(
  categoryList: Array<CategoryType>
): Array<OptionType> {
  return categoryList.map((category) => ({
    value: String(category.id),
    label: getNameWithDepth(category.name, category.depth),
  }));
}

export function convertCategoryToRepeaterIncomingValue(
  relatedList: Array<CategoryRelatedType>
) {
  return relatedList.map((related) => {
    return [
      { name: "category", value: String(related.category.id) },
      { name: "label", value: related.label },
    ];
  });
}

/** Related category table */

export function categoryFieldConfig(
  categoryNoChildrenOptionList: Array<OptionType>
): RepeaterFieldConfig {
  return {
    name: "categories",
    type: "REPEATER",
    label: "Связанные категории",
    meta: {
      view: "TABLE",
      defaultIsOpen: true,
    },
    fields: [
      {
        name: "category",
        type: "SELECT",
        label: "Категория",
        meta: { options: categoryNoChildrenOptionList },
      },
      {
        name: "label",
        type: "STRING",
        label: "Текст ссылки",
        meta: {},
      },
    ],
  };
}
